/**
 * Seite "Aktivitaeten": das Protokoll aller Aenderungen als Tabelle, Stueck fuer Stueck nachladbar
 * (`GET /api/activities` ueber eine Fortsetzungsmarke, `api/aktivitaeten.ts`). Neubau ohne
 * Bestandsvorbild. Die Filter nimmt die Seite aus der `ansicht` (Adresse) entgegen und reicht sie an die
 * API; bedient werden sie in `AktivitaetenFilterleiste.tsx` (Task 8). Die Seite laedt auch deren
 * Auswahllisten (`aktivitaetenFilterOptionenLaden`) und fuehrt "Aktualisieren" und den CSV-Export aus -
 * die Leiste selbst kennt weder API noch Export.
 *
 * DER FILTERZUSTAND LEBT IN DER ADRESSE (`state/adresse.ts#AktivitaetenAnsicht`), nicht hier. Die
 * Seite ist kontrolliert: `ansicht` kommt von aussen, jede Aenderung geht ueber `onAnsichtAendern`
 * zurueck. Die Seitenansicht nennt die Sortierrichtung `dir`, die API-Abfrage `sort` - `abfrageAus`
 * bildet das ab.
 *
 * LADEN: nur bei `istAktiveSeite`. Jeder Wechsel der Abfrage (Filter, Sortierung) bricht die laufende
 * Anfrage ab (`AbortController`) und laedt ab Anfang neu. Der Wechsel wird am TEXT der Abfrage
 * erkannt (`aktivitaetenAbfrageText`), nicht an der Identitaet des `ansicht`-Objekts: `useAnsicht`
 * liefert bei jeder Adressaenderung (z. B. ein geoeffneter Drawer) ein neues Objekt, und das darf
 * nicht neu laden.
 *
 * VERTAUSCHTER ZEITRAUM (Zweig-Fix, Ruling 36): Liegt `von` nach `bis`, oder wird ein Jahr noch getippt
 * (unter 1000, `lib/aktivitaetenZeitraum.ts`), ist das ein Eingabezustand, kein Ladefehler. Die Seite stellt
 * dann KEINE Anfrage - weder Laden noch Nachladen, CSV oder Aktualisieren - und rechnet mit der zuletzt
 * gueltigen Abfrage weiter: `abfrageRef` und damit der Schluessel des Lade-Effekts frieren ein, eine laufende
 * Anfrage wird nicht abgebrochen, die zuletzt geladene Liste bleibt stehen. Den Hinweis "Von liegt nach Bis"
 * zeigt die Filterleiste an den Datumsfeldern. Stimmt der Zeitraum wieder, laedt die Seite bei geaendertem
 * Schluessel wie gewohnt (eine einzige Anfrage). Der Server bleibt bei 400 (Direktaufrufe).
 *
 * OBJEKTE (Phase 2, Task 11B): neben Bestellungen protokolliert das Backend Aenderungen an Nutzern, Lieferanten,
 * Zahlungsvorlagen, FX und weiteren Objekten (`wo.art`). Der Filter `wo` geht in die Abfrage; was ein Klick auf die
 * Zeile tut, entscheidet `aktivitaetenAnzeige.ts#klickZiel` anhand der Rechte (`rechte`): Bestellung in den Drawer
 * (`onBestellungOeffnen`), alles mit eigener Seite dorthin (`onSeiteOeffnen`). Die Seite selbst wechselt nichts - das
 * verdrahtet `App.tsx`.
 *
 * Diese Seite importiert ausschliesslich aus `api/aktivitaeten.ts`. Das gleichnamige `aktivitaetenLaden`
 * der alten Uebersichtskachel (`api/historie.ts`) ist mit Task 9 entfallen.
 */
import { useEffect, useRef, useState, type JSX } from "react";
import { Seitenkopf } from "../App/Seitenkopf.tsx";
import {
  aktivitaetenAbfrageText,
  aktivitaetenExportieren,
  aktivitaetenFilterOptionenLaden,
  aktivitaetenLaden,
  type Aktivitaet,
  type AktivitaetenAbfrage,
  type AktivitaetenFilterOptionen,
  type AktivitaetWo,
} from "../../api/aktivitaeten.ts";
import { berlinHeute } from "../../lib/berlinTag.ts";
import { zeitraumPruefen } from "../../lib/aktivitaetenZeitraum.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import { addDays } from "../../lib/format.ts";
import type { AktivitaetenAnsicht } from "../../state/adresse.ts";
import { AktivitaetenFilterleiste } from "./AktivitaetenFilterleiste.tsx";
import { AktivitaetenTabelle } from "./AktivitaetenTabelle.tsx";
import type { SeitenZiel } from "./aktivitaetenAnzeige.ts";

export interface AktivitaetenSeiteProps {
  /** Geladen wird nur, solange die Seite sichtbar ist. */
  istAktiveSeite: boolean;
  /** Klick auf eine oeffenbare Bestellung oder Sendung (`App.tsx#aktivitaetOeffnen`). */
  onBestellungOeffnen: (wo: AktivitaetWo) => void;
  /** Klick auf ein Objekt mit eigener Seite - Nutzer, Lieferant, Zahlungsvorlage, FX (`App.tsx#aktivitaetSeiteOeffnen`). */
  onSeiteOeffnen: (ziel: SeitenZiel) => void;
  /** Die Rechte des Nutzers: sie entscheiden in der Tabelle, welche Objekte anklickbar sind (`klickZiel`). */
  rechte: ReadonlySet<string>;
  ansicht: AktivitaetenAnsicht;
  onAnsichtAendern: (a: AktivitaetenAnsicht) => void;
}

/**
 * Der Berliner Tag-Zeitraum der Ansicht: gesetztes `von`/`bis` gewinnt gegen die Kurzwahl (die dann
 * ignoriert wird, auch wenn nur eine der beiden Grenzen gesetzt ist); sonst heute, die letzten 7 Tage
 * (heute minus 6 bis heute), die letzten 30 Tage (heute minus 29 bis heute) oder alles. `heute` kommt
 * vom Aufrufer: `berlinHeute()`, der Kalendertag in Europe/Berlin - NICHT `todayIso()` (UTC-Tag, um
 * 00:30 MESZ noch der Vortag) und nicht `localIsoDay` (Zeitzone des Browsers). Der Server filtert
 * ebenfalls nach Berliner Tagen. "Heute minus n" ist reine Kalenderrechnung auf dem `YYYY-MM-DD`-Tag
 * (`addDays` rechnet in UTC auf dem Datumstext), kippt also an der Zeitumstellung nicht.
 */
export function zeitraumAusAnsicht(a: AktivitaetenAnsicht, heute: string): { von?: string; bis?: string } {
  if (a.von || a.bis) {
    const zeitraum: { von?: string; bis?: string } = {};
    if (a.von) zeitraum.von = a.von;
    if (a.bis) zeitraum.bis = a.bis;
    return zeitraum;
  }
  switch (a.zr) {
    case "heute":
      return { von: heute, bis: heute };
    case "7t":
      return { von: addDays(heute, -6), bis: heute };
    case "30t":
      return { von: addDays(heute, -29), bis: heute };
    case "alle":
      return {};
  }
}

/**
 * Die API-Abfrage zur Seitenansicht: Zeitraum aus `zr`/`von`/`bis`, `dir` wird zu `sort`. Der Suchtext geht
 * getrimmt hinaus (Review zu Task 6: die API-Schicht schneidet nichts ab, ein `q` aus Leerzeichen wuerde
 * sonst als `q=++` gesendet); nach dem Trimmen leer, gibt es kein `q`. Liste, Nachladen und CSV rechnen
 * alle ueber diese eine Abfrage.
 */
function abfrageAus(ansicht: AktivitaetenAnsicht): AktivitaetenAbfrage {
  const q = ansicht.q.trim();
  return {
    ...zeitraumAusAnsicht(ansicht, berlinHeute()),
    wer: ansicht.wer,
    bereich: ansicht.bereich,
    feld: ansicht.feld,
    wo: ansicht.wo,
    quelle: ansicht.quelle,
    ...(q ? { q } : {}),
    sort: ansicht.dir,
  };
}

/** "<n> Änderungen" - 1 im Singular, de-DE-Tausenderpunkt, Untergrenze als "mehr als 10.000". */
function trefferText(gesamt: number, istUntergrenze: boolean): string {
  if (istUntergrenze) return "mehr als 10.000 Änderungen";
  return gesamt === 1 ? "1 Änderung" : `${gesamt.toLocaleString("de-DE")} Änderungen`;
}

interface Stand {
  /** Zustand des ERSTEN Stuecks; die Folgestuecke laufen ueber `weitereLaedt`/`weitereFehler`. */
  phase: "laedt" | "fertig" | "fehler";
  /**
   * Der Text der Fehlerzeile beim ersten Laden (fester Satz + Ursache aus `fehlertext()`); nur in der Phase
   * "fehler" belegt. `null` dort heisst: `fehlertext` ergab `null` (Abbruch, 401) - keine Zeile.
   */
  fehler: string | null;
  eintraege: Aktivitaet[];
  /** Marke fuers naechste Stueck; `null` = keine weiteren Eintraege. */
  nach: string | null;
  gesamt: number | null;
  gesamtIstUntergrenze: boolean;
  jtlSucheAusgefallen: boolean;
  jtlSucheGekappt: boolean;
  weitereLaedt: boolean;
  /** Der Text der Fehlerzeile beim Nachladen; `null` = keine Zeile (auch, wenn `fehlertext` `null` ergab). */
  weitereFehler: string | null;
}

const LAEDT: Stand = {
  phase: "laedt",
  fehler: null,
  eintraege: [],
  nach: null,
  gesamt: null,
  gesamtIstUntergrenze: false,
  jtlSucheAusgefallen: false,
  jtlSucheGekappt: false,
  weitereLaedt: false,
  weitereFehler: null,
};

export function AktivitaetenSeite({
  istAktiveSeite,
  onBestellungOeffnen,
  onSeiteOeffnen,
  rechte,
  ansicht,
  onAnsichtAendern,
}: AktivitaetenSeiteProps): JSX.Element {
  const [stand, setStand] = useState<Stand>(LAEDT);
  /** Zaehler fuer "Erneut versuchen" beim ersten Stueck und fuer "Aktualisieren" (beides laedt von vorn). */
  const [versuch, setVersuch] = useState(0);
  /** Die Auswahllisten der Filterleiste; `null` bis geladen und solange nur Fehlschlaege vorliegen. */
  const [optionen, setOptionen] = useState<AktivitaetenFilterOptionen | null>(null);
  /**
   * Der Text zum gescheiterten Abruf der Auswahllisten (fester Satz + Ursache); die Leiste zeigt ihn nur, solange
   * `optionen` fehlt. `null` = kein Fehler (auch, wenn `fehlertext` `null` ergab).
   */
  const [optionenFehler, setOptionenFehler] = useState<string | null>(null);
  const [exportLaeuft, setExportLaeuft] = useState(false);
  /** Der Text der Fehlerzeile unter der Filterleiste; `null` = keine Zeile. */
  const [exportFehler, setExportFehler] = useState<string | null>(null);

  // Ein vertauschter oder noch getippter Zeitraum friert die Abfrage ein - siehe Kopfkommentar. `null`, solange
  // es noch keine gueltige gab (Seite mit vertauschtem Zeitraum aus der Adresse geoeffnet).
  const pausiert = zeitraumPruefen(ansicht.von, ansicht.bis) !== "ok";
  const abfrageRef = useRef<AktivitaetenAbfrage | null>(null);
  if (!pausiert) abfrageRef.current = abfrageAus(ansicht);
  // Der Effekt liest die Abfrage ueber die Ref und haengt nur am Text - siehe Kopfkommentar.
  const schluessel = abfrageRef.current ? aktivitaetenAbfrageText(abfrageRef.current) : null;
  /** Die Anfrage des laufenden "Weitere laden"; der Effekt unten bricht sie beim Filterwechsel ab. */
  const nachladenAbbruch = useRef<AbortController | null>(null);

  useEffect(() => {
    const abfrage = abfrageRef.current;
    if (!istAktiveSeite || abfrage === null) return;
    const abbruch = new AbortController();
    setStand(LAEDT);
    aktivitaetenLaden(abfrage, null, abbruch.signal).then(
      (antwort) => {
        if (abbruch.signal.aborted) return;
        setStand({
          phase: "fertig",
          fehler: null,
          eintraege: antwort.eintraege,
          nach: antwort.nach,
          gesamt: antwort.gesamt,
          gesamtIstUntergrenze: antwort.gesamtIstUntergrenze,
          jtlSucheAusgefallen: antwort.jtlSucheAusgefallen,
          jtlSucheGekappt: antwort.jtlSucheGekappt,
          weitereLaedt: false,
          weitereFehler: null,
        });
      },
      (ursache: unknown) => {
        if (abbruch.signal.aborted) return;
        // Die Fehlerzeile mit "Erneut versuchen" gab es schon (Spec R2, "Inline bleibt inline"): neu ist die
        // Ursache dahinter. Ergibt `fehlertext` `null` (Abbruch), erscheint keine Zeile.
        setStand({ ...LAEDT, phase: "fehler", fehler: fehlertext(ursache, "Aktivitäten konnten nicht geladen werden.") });
      },
    );
    return () => {
      abbruch.abort();
      nachladenAbbruch.current?.abort();
      nachladenAbbruch.current = null;
    };
  }, [istAktiveSeite, schluessel, versuch]);

  // Die Auswahllisten laden mit der Seite und bei jedem "Aktualisieren". Scheitert der Abruf, bleibt die
  // Seite benutzbar: Wer und Bereich melden es ("Optionen konnten nicht geladen werden."), Quelle hat ihre
  // Standardliste, und "Aktualisieren" versucht es erneut. Schon geladene Listen bleiben bei einem
  // spaeteren Fehlschlag stehen.
  useEffect(() => {
    if (!istAktiveSeite) return;
    const abbruch = new AbortController();
    setOptionenFehler(null);
    aktivitaetenFilterOptionenLaden(abbruch.signal).then(
      (liste) => {
        if (!abbruch.signal.aborted) setOptionen(liste);
      },
      (ursache: unknown) => {
        // Der Hinweis in der Filterleiste gab es schon (Spec R2): neu ist die Ursache dahinter. Ergibt
        // `fehlertext` `null` (Abbruch), erscheint keine Fehlerzeile.
        if (!abbruch.signal.aborted) setOptionenFehler(fehlertext(ursache, "Optionen konnten nicht geladen werden."));
      },
    );
    return () => abbruch.abort();
  }, [istAktiveSeite, versuch]);

  /** CSV zu denselben Filtern wie die Liste, ohne Marke - der Export umfasst alle Treffer. */
  async function exportieren(): Promise<void> {
    const abfrage = abfrageRef.current;
    if (exportLaeuft || pausiert || abfrage === null) return;
    setExportLaeuft(true);
    setExportFehler(null);
    let meldung: string | null = null;
    try {
      await aktivitaetenExportieren(abfrage);
    } catch (ursache) {
      // Die Fehlerzeile unter der Filterleiste gab es schon (Spec R2, "Inline bleibt inline"): neu ist
      // nur der Text. Der feste Satz wird zum Kontext, die Ursache kommt aus `fehlertext()`; ergibt der
      // `null` (Abbruch), bleibt die Zeile weg.
      meldung = fehlertext(ursache, "CSV konnte nicht erstellt werden.");
    }
    setExportLaeuft(false);
    setExportFehler(meldung);
  }

  function weitereLaden(): void {
    const nach = stand.nach;
    const abfrage = abfrageRef.current;
    if (nach === null || stand.weitereLaedt || pausiert || abfrage === null) return;
    const abbruch = new AbortController();
    nachladenAbbruch.current = abbruch;
    setStand((alt) => ({ ...alt, weitereLaedt: true, weitereFehler: null }));
    aktivitaetenLaden(abfrage, nach, abbruch.signal).then(
      (antwort) => {
        if (abbruch.signal.aborted) return;
        setStand((alt) => ({
          ...alt,
          eintraege: [...alt.eintraege, ...antwort.eintraege],
          nach: antwort.nach,
          // `gesamt` zaehlt nur das erste Stueck; die Hinweise bleiben, wenn ein Stueck sie meldet.
          jtlSucheAusgefallen: alt.jtlSucheAusgefallen || antwort.jtlSucheAusgefallen,
          jtlSucheGekappt: alt.jtlSucheGekappt || antwort.jtlSucheGekappt,
          weitereLaedt: false,
          weitereFehler: null,
        }));
      },
      (ursache: unknown) => {
        if (abbruch.signal.aborted) return;
        // Wie beim ersten Laden: die Zeile gab es schon, neu ist die Ursache. `null` (Abbruch) = keine Zeile.
        setStand((alt) => ({
          ...alt,
          weitereLaedt: false,
          weitereFehler: fehlertext(ursache, "Aktivitäten konnten nicht geladen werden."),
        }));
      },
    );
  }

  function richtungWaehlen(dir: AktivitaetenAnsicht["dir"]): void {
    if (ansicht.dir !== dir) onAnsichtAendern({ ...ansicht, dir });
  }

  return (
    <div className="ov-wrap akt-seite">
      <Seitenkopf titel="Aktivitäten" />
      <AktivitaetenFilterleiste
        ansicht={ansicht}
        optionen={optionen}
        optionenFehler={optionenFehler}
        onAendern={onAnsichtAendern}
        onAktualisieren={() => setVersuch((v) => v + 1)}
        onExport={() => void exportieren()}
        exportLaeuft={exportLaeuft}
      />
      {exportFehler ? (
        <div className="empty-note akt-hinweis akt-fehler" role="alert">
          {exportFehler}
        </div>
      ) : null}
      <div className="akt-leiste">
        <span className="akt-anzahl">
          {stand.phase === "fertig" && stand.gesamt !== null
            ? trefferText(stand.gesamt, stand.gesamtIstUntergrenze)
            : ""}
        </span>
        <div className="akt-segment" role="group" aria-label="Sortierung">
          <button type="button" aria-pressed={ansicht.dir === "desc"} onClick={() => richtungWaehlen("desc")}>
            Neueste zuerst
          </button>
          <button type="button" aria-pressed={ansicht.dir === "asc"} onClick={() => richtungWaehlen("asc")}>
            Älteste zuerst
          </button>
        </div>
      </div>

      {stand.phase === "fertig" && stand.jtlSucheAusgefallen ? (
        <div className="empty-note akt-hinweis" role="status">
          Die Suche in JTL ist gerade nicht erreichbar – gesucht wurde nur in den Hub-Daten.
        </div>
      ) : null}
      {stand.phase === "fertig" && stand.jtlSucheGekappt ? (
        <div className="empty-note akt-hinweis" role="status">
          Die Suche passt auf sehr viele Bestellungen; berücksichtigt sind die ersten 10.000.
        </div>
      ) : null}

      {stand.phase === "laedt" ? (
        <div className="empty-note" role="status">
          {schluessel === null ? "Die Liste wird geladen, sobald der Zeitraum stimmt." : "Wird geladen …"}
        </div>
      ) : stand.phase === "fehler" ? (
        stand.fehler !== null ? (
          <div className="empty-note akt-fehler" role="alert">
            <p>{stand.fehler}</p>
            <button type="button" className="btn-ghost" disabled={pausiert} onClick={() => setVersuch((v) => v + 1)}>
              Erneut versuchen
            </button>
          </div>
        ) : null
      ) : stand.eintraege.length === 0 ? (
        <div className="empty-note">Keine Änderungen für diese Filter</div>
      ) : (
        <>
          <AktivitaetenTabelle
            eintraege={stand.eintraege}
            onBestellungOeffnen={onBestellungOeffnen}
            onSeiteOeffnen={onSeiteOeffnen}
            rechte={rechte}
          />
          {stand.weitereFehler !== null ? (
            <div className="akt-mehr akt-fehler" role="alert">
              <p>{stand.weitereFehler}</p>
              <button type="button" className="btn-ghost" disabled={pausiert} onClick={weitereLaden}>
                Erneut versuchen
              </button>
            </div>
          ) : stand.nach !== null ? (
            <div className="akt-mehr">
              <button type="button" className="btn-ghost" disabled={stand.weitereLaedt || pausiert} onClick={weitereLaden}>
                Weitere 50 laden
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
